import { eventQBus } from "../../types/EventQBus";
import { fireFilterSubmit } from "../FilterFormActions";
import { isSecondLevelActive } from "../../hcSheet/HcAllLevelSheet";
import type { TrackingLabels } from "../../tracking/TrackingLabels";
import DelayedAction from "../../util/DelayedAction";
import { element } from "../../util/Utils";
import { HeurekaElementFactory } from "../../util/HeurekaElementFactory";
import tracker from "../../tracking/Tracker";
import { Filter } from "../Filter";
import { filterFooter } from "../../filtersheet/Selectors";
import { events } from "../../events";

const CUSTOM_RANGE_CLASS = "hc_customRange";
const CUSTOM_RANGE_SELECTOR = `.${CUSTOM_RANGE_CLASS}`;
const CUSTOM_RANGE_INPUT_CLASS = `${CUSTOM_RANGE_CLASS}__input`;
const MINMAX_FIELD_SELECTOR = "input[type=number], oc-text-field-v1";

/*                                                                          */
type MinMaxField = HTMLInputElement | (HTMLElement & { value: string });

export class CustomRangeInput {
  static readonly factory = HeurekaElementFactory.byElement("input", CUSTOM_RANGE_INPUT_CLASS, CustomRangeInput);

  static allRangesValid(filterForm: HTMLFormElement): boolean {
    return CustomRangeInput.factory
      .all(filterForm)
      .every(
        (customRangeInput) =>
          customRangeInput.elem.dataset.valid === "true" || customRangeInput.elem.dataset.valid === undefined,
      );
  }

  /*               */
  constructor(
    readonly elem: HTMLInputElement,
    readonly form: HTMLFormElement | null = elem.form,
    readonly customRange: HTMLElement | null = elem.closest(CUSTOM_RANGE_SELECTOR),
    readonly minField: MinMaxField | null = customRange?.querySelectorAll<MinMaxField>(MINMAX_FIELD_SELECTOR)[0] ||
      null,
    readonly maxField: MinMaxField | null = customRange?.querySelectorAll<MinMaxField>(MINMAX_FIELD_SELECTOR)[1] ||
      null,
  ) {}

  /*                  */
  static fromId(sliderId: string, root?: ParentNode) {
    return CustomRangeInput.factory.declare(element(`#${sliderId}`, root)?.closest(CUSTOM_RANGE_SELECTOR));
  }

  static init(elem: HTMLElement) {
    return CustomRangeInput.factory.pick(undefined, elem)?.init();
  }

  /*               */

  static registerDeprecated() {
    eventQBus.on("heureka.sliders.initialised", CustomRangeInput.initAll);
  }

  static register() {
    events.onFilterSectionLoaded(CustomRangeInput.addInputListener);
  }

  private static initAll() {
    CustomRangeInput.factory.all().forEach((customRangeInput) => customRangeInput.init());
  }

  private setupRangeValidation() {
    const filter = Filter.factory.closest(this.elem);

    const validateRange = () => {
      if (CustomRangeInput.allRangesValid(filter.form)) {
        filter.hideError();
      }
      this.updateSubmitButtonState(filter);
    };

    document.addEventListener("heureka.customRange.validityChanged", (event) => {
      const customEvent = event as CustomEvent<{ filterId: string }>;
      const eventFilterId = customEvent.detail?.filterId;
      /*                                                           */
      if (eventFilterId && this.customRange && eventFilterId === filter.id) {
        validateRange();
      }
    });

    /*                                                                       */
    document.addEventListener("heureka.filterMessageBanner.dismiss", (event) => {
      const customEvent = event as CustomEvent<{ filterId: string }>;
      const filterId = customEvent.detail?.filterId;
      /*                                                                   */
      if (filterId === this.customRange.dataset.filterId) {
        this.resetInputValues(filter);
      }
    });
  }

  private resetInputValues(filter: Filter) {
    if (this.minField.value > this.maxField.value) {
      /*                        */
      this.minField.value = "";
      this.maxField.value = "";
      /*                          */
      const hiddenInput = this.elem.closest(".hc_facetValueInput") as HTMLInputElement;
      if (hiddenInput) {
        hiddenInput.value = "_,_";
      }
      this.updateSubmitButtonState(filter);
    }
  }

  private updateSubmitButtonState(filter: Filter) {
    const submitButton = filter.filterSelectButton;
    if (submitButton) {
      if (CustomRangeInput.allRangesValid(filter.form)) {
        submitButton.disabled = false;
      } else {
        submitButton.disabled = true;
      }
    }
  }

  private init() {
    if (this.elem.dataset.heurekaSliderInitialized) {
      /*                   */
      return;
    }
    this.elem.dataset.heurekaSliderInitialized = "true";
    if (this.customRange) {
      this.initializeAutoSubmit();

      this.initializeInputField(this.minField, 0);
      this.initializeInputField(this.maxField, 1);
    }
    this.setupRangeValidation();
    return this;
  }

  private initializeAutoSubmit() {
    if (this.form && this.customRange) {
      const delayedAction = DelayedAction.create(this.form, () => this.fireSliderAutoSubmit("adopt_delayed"));

      const doCancel = delayedAction.cancel.bind(delayedAction);
      const doDelay = delayedAction.delay.bind(delayedAction, {});
      const doDelayNoWait = delayedAction.delay.bind(delayedAction, {
        delayInMillis: 0,
        actionToPerform: () => this.fireSliderAutoSubmit(), /*                          */
      });
      delayedAction.throttle.bind(delayedAction, {
        throttleInMillis: 50,
      });
      const options = { passive: true };

      /*                                                                                                  */
      this.customRange.addEventListener("pointerdown", doCancel, options);
      this.customRange.addEventListener("pointerup", doDelay, options);

      this.customRange.addEventListener("focusin", doCancel, options);
      this.customRange.addEventListener("focusout", doDelayNoWait, options);

      this.form.addEventListener(
        "click",
        (event) => {
          if (event.target === this.minField || event.target === this.maxField) doCancel();
        },
        options,
      );

      this.form.addEventListener("filterSubmit", doCancel, options);
      this.form.addEventListener("reset", doCancel, options);
    }
  }

  private initializeInputField(inputField: MinMaxField | undefined | null, limitIndex: number) {
    if (this.customRange && inputField) {
      const limitType: "min" | "max" = limitIndex === 0 ? "min" : "max";
      const { facetName } = this.customRange.dataset;
      if (facetName) {
        inputField.addEventListener("focusin", this.trackFocus(`click_${limitType}`, facetName), {
          passive: true,
        });
        inputField.addEventListener("input", this.trackDelete(`delete_${limitType}`, facetName), {
          passive: true,
        });
      }
    }
  }

  private fireSliderAutoSubmit(filterMethod?: string) {
    /*                                                  */
    if (isSecondLevelActive()) {
      return false;
    }
    if (this.form && this.form.closest(".heureka_filterTitle__values")) {
      if (filterMethod) {
        this.form.dataset.tsFeatureFilterMethod = filterMethod;
      }
      return fireFilterSubmit(this.form);
    }
    return false;
  }

  /*                       */

  get facetName(): string {
    return this.customRange?.dataset.facetName || "";
  }

  get footer(): HTMLElement | null {
    return filterFooter(this.form);
  }

  get selectButton(): HTMLElement | null | undefined {
    return this.footer?.querySelector(".ts_heureka_selectRange");
  }

  addInputListener() {
    this.customRange?.addEventListener("input", (event) => {
      const hasInput =
        this.minField?.value !== this.minField?.dataset?.defaultValue ||
        this.maxField?.value !== this.maxField?.dataset?.defaultValue;
      if (event.target === this.minField || event.target === this.maxField) {
        this.selectButton?.classList.toggle("find_filter__select--hidden", !hasInput);
      }
    });
  }

  /*       */

  private trackFocus(
    sanFacetActivity: TrackingLabels["san_FacetActivity"],
    sanFacetActivityType: TrackingLabels["san_FacetActivityType"],
  ): EventListener {
    return () => {
      tracker.submitEvent({
        san_FacetActivity: sanFacetActivity,
        san_FacetActivityType: sanFacetActivityType,
      });
    };
  }

  private trackDelete(
    sanFacetActivity: TrackingLabels["san_FacetActivity"],
    sanFacetActivityType: TrackingLabels["san_FacetActivityType"],
  ): EventListener {
    return (event) => {
      const field = event.currentTarget as MinMaxField | null;
      if (field && !field.value) {
        tracker.submitEvent({
          san_FacetActivity: sanFacetActivity,
          san_FacetActivityType: sanFacetActivityType,
        });
      }
    };
  }

  private static addInputListener() {
    CustomRangeInput.factory.all().forEach((customRangeInput) => customRangeInput.addInputListener());
  }
}
